import {
  Translation,
  TranslocoLoader,
  TranslocoModule,
  provideTransloco
} from "@jsverse/transloco";
import { inject, Injectable, NgModule } from "@angular/core";
import { environment } from "src/environments/environment";
import { provideTranslocoMessageformat } from "@jsverse/transloco-messageformat";
import { BabelBackend } from "src/app/backend/api/babel.backend";
import { lastValueFrom } from "rxjs";
import { TRANSLATIONS_TEMP } from "src/app/transloco/transloco-temp-translations";

@Injectable({ providedIn: "root" })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly babelBackend = inject(BabelBackend);

  async getTranslation(lang: string): Promise<Translation> {
    switch (lang) {
      case "it": {
        const [base, diagnostic]: [Translation, Translation] =
          await Promise.all([
            this.loadBabel("it-IT"),
            this.loadBabelDiagnostic("it-IT")
          ]);

        return {
          ...base,
          ...diagnostic,
          ...TRANSLATIONS_TEMP.it
        };
      }
      case "en": {
        const [assets, base, diagnostic]: [
          Translation,
          Translation,
          Translation
        ] = await Promise.all([
          this.loadAsset("/assets/i18n/en.json"),
          this.loadBabel("en-US"),
          this.loadBabelDiagnostic("en-US")
        ]);

        return {
          ...assets,
          ...base,
          ...diagnostic,
          ...TRANSLATIONS_TEMP.en
        };
      }
      case "es": {
        const [assets, base, diagnostic]: [
          Translation,
          Translation,
          Translation
        ] = await Promise.all([
          this.loadAsset("/assets/i18n/es.json"),
          this.loadBabel("es-ES"),
          this.loadBabelDiagnostic("es-ES")
        ]);

        return {
          ...assets,
          ...base,
          ...diagnostic,
          ...TRANSLATIONS_TEMP.es
        };
      }
      case "fr": {
        const [base, diagnostic]: [Translation, Translation] =
          await Promise.all([
            this.loadBabel("fr-FR"),
            this.loadBabelDiagnostic("fr-FR")
          ]);

        return {
          ...base,
          ...diagnostic,
          ...TRANSLATIONS_TEMP.fr
        };
      }
      case "ka": {
        const [en, base, diagnostic, local]: [
          Translation,
          Translation,
          Translation,
          Translation
        ] = await Promise.all([
          this.loadAsset("/assets/i18n/en.json"),
          this.loadBabel("en-US"),
          this.loadBabelDiagnostic("en-US"),
          this.loadAsset("/assets/i18n/ka.json")
        ]);

        return {
          ...en,
          ...base,
          ...diagnostic,
          ...TRANSLATIONS_TEMP.en,
          ...local
        };
      }
      default: {
        return Promise.resolve({});
      }
    }
  }

  private async loadAsset(path: string): Promise<Translation> {
    try {
      const response: Response = await fetch(path);
      if (!response.ok) {
        return {};
      }

      const translation: Translation = (await response.json()) as Translation;

      return translation;
    } catch {
      return {};
    }
  }

  private async loadBabel(lang: string): Promise<Translation> {
    try {
      const translation: Translation = await lastValueFrom(
        this.babelBackend.load(lang)
      );

      return translation;
    } catch {
      return {};
    }
  }

  private async loadBabelDiagnostic(lang: string): Promise<Translation> {
    try {
      const translation: Translation = await lastValueFrom(
        this.babelBackend.loadDiagnostic(lang)
      );

      return translation;
    } catch {
      return {};
    }
  }
}

@NgModule({
  exports: [TranslocoModule],
  providers: [
    provideTransloco({
      config: {
        availableLangs: environment.snoots.languages.supported,
        defaultLang: environment.snoots.languages.default,
        fallbackLang: [environment.snoots.languages.default],
        failedRetries: 2,
        reRenderOnLangChange: false,
        prodMode: environment.snoots.production,
        missingHandler: {
          allowEmpty: true,
          logMissingKey: false
        }
      },
      loader: TranslocoHttpLoader
    }),
    provideTranslocoMessageformat({
      locales: environment.snoots.languages.supported
    })
  ]
})
export class TranslocoRootModule {}
